import { useCallback, useEffect, useRef, useState } from "react";
import { ResponseError } from "../services/ApiClient";
import {
  canCancelVoiceRecording,
  VoiceRecordingStatus,
} from "../components/AiChat/voiceRecordingState";

export type { VoiceRecordingStatus } from "../components/AiChat/voiceRecordingState";

export type VoiceFinishMode = "insert" | "send";

type VoiceSession = {
  id: number;
  stream: MediaStream;
  recorder: MediaRecorder;
  audioContext: AudioContext;
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  levelData: Uint8Array<ArrayBuffer>;
  chunks: Blob[];
  startedAt: number;
  trackEndedHandler: () => void;
  maxDurationTimer: ReturnType<typeof setTimeout> | null;
  finishMode: VoiceFinishMode | null;
};

type UseVoiceRecordingOptions = {
  transcribe: (audio: Blob) => Promise<string>;
  onFinished: (transcript: string, mode: VoiceFinishMode) => Promise<void> | void;
  onError: (message: string) => void;
  maxDurationSeconds?: number;
};

const MIME_TYPE = "audio/webm;codecs=opus";
const ENCODER_SAFETY_MARGIN_MS = 1000;

const getUserMediaErrorMessage = (error: unknown): string => {
  if (error instanceof DOMException) {
    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      return "Der Mikrofonzugriff wurde verweigert. Bitte erlaube den Zugriff in den Browsereinstellungen.";
    }
    if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
      return "Es wurde kein Mikrofon gefunden.";
    }
    if (error.name === "NotReadableError") {
      return "Das Mikrofon ist derzeit nicht verfügbar.";
    }
  }

  return "Die Aufnahme konnte nicht gestartet werden.";
};

const releaseSessionResources = (session: VoiceSession) => {
  if (session.maxDurationTimer !== null) {
    clearTimeout(session.maxDurationTimer);
    session.maxDurationTimer = null;
  }
  session.stream.getTracks().forEach((track) => {
    track.removeEventListener("ended", session.trackEndedHandler);
    track.stop();
  });
  try {
    session.source.disconnect();
    session.analyser.disconnect();
  } catch {
    // The audio graph may already be disconnected during an idempotent cleanup.
  }
  if (session.audioContext.state !== "closed") {
    session.audioContext.close().catch(() => {});
  }
};

const getTranscriptionErrorMessage = (error: unknown): string => {
  if (error instanceof ResponseError && error.message) {
    return error.message;
  }

  return "Die Transkription ist fehlgeschlagen.";
};

export const useVoiceRecording = ({
  transcribe,
  onFinished,
  onError,
  maxDurationSeconds = 300,
}: UseVoiceRecordingOptions) => {
  const [status, setStatusState] = useState<VoiceRecordingStatus>("idle");
  const statusRef = useRef<VoiceRecordingStatus>("idle");
  const sessionRef = useRef<VoiceSession | null>(null);
  const sessionIdRef = useRef(0);
  const mountedRef = useRef(true);
  const transcribeRef = useRef(transcribe);
  const onFinishedRef = useRef(onFinished);
  const onErrorRef = useRef(onError);
  transcribeRef.current = transcribe;
  onFinishedRef.current = onFinished;
  onErrorRef.current = onError;

  const setStatus = useCallback((nextStatus: VoiceRecordingStatus) => {
    statusRef.current = nextStatus;
    if (mountedRef.current) {
      setStatusState(nextStatus);
    }
  }, []);

  const cancel = useCallback(() => {
    if (!canCancelVoiceRecording(statusRef.current)) {
      return;
    }

    sessionIdRef.current++;
    const session = sessionRef.current;
    sessionRef.current = null;

    if (session) {
      session.recorder.ondataavailable = null;
      session.recorder.onstop = null;
      session.recorder.onerror = null;
      if (session.recorder.state !== "inactive") {
        try {
          session.recorder.stop();
        } catch {
          // The recorder may already be stopping; resource cleanup below is still sufficient.
        }
      }
      releaseSessionResources(session);
      session.chunks.length = 0;
    }

    // A running transcription request is not aborted; its result is discarded via the bumped session id (LB-8333).
    setStatus("idle");
  }, [setStatus]);

  const finishRecording = useCallback(
    async (session: VoiceSession) => {
      if (session.id !== sessionIdRef.current) {
        releaseSessionResources(session);
        return;
      }

      releaseSessionResources(session);
      const mode = session.finishMode;
      if (!mode) {
        sessionRef.current = null;
        setStatus("idle");
        return;
      }

      const audioBlob = new Blob(session.chunks, { type: MIME_TYPE });
      session.chunks.length = 0;
      if (audioBlob.size === 0) {
        sessionRef.current = null;
        setStatus("error");
        onErrorRef.current("Die Aufnahme konnte nicht verarbeitet werden.");
        return;
      }

      try {
        const transcript = (await transcribeRef.current(audioBlob)).trim();

        if (session.id !== sessionIdRef.current) {
          return;
        }

        if (transcript === "") {
          sessionRef.current = null;
          setStatus("error");
          onErrorRef.current("Es wurde keine Sprache erkannt.");
          return;
        }

        if (mode === "send") {
          setStatus("sending");
        }
        await onFinishedRef.current(transcript, mode);
        if (session.id === sessionIdRef.current) {
          sessionRef.current = null;
          setStatus("idle");
        }
      } catch (error) {
        if (session.id !== sessionIdRef.current) {
          return;
        }
        sessionRef.current = null;
        setStatus("error");
        onErrorRef.current(getTranscriptionErrorMessage(error));
      }
    },
    [setStatus],
  );

  const stop = useCallback(
    (mode: VoiceFinishMode) => {
      const session = sessionRef.current;
      if (!session || statusRef.current !== "recording") {
        return;
      }

      session.finishMode = mode;
      setStatus("transcribing");
      try {
        session.recorder.stop();
      } catch {
        sessionIdRef.current++;
        sessionRef.current = null;
        releaseSessionResources(session);
        setStatus("error");
        onErrorRef.current("Die Aufnahme konnte nicht verarbeitet werden.");
      }
    },
    [setStatus],
  );

  const start = useCallback(async () => {
    if (sessionRef.current || !["idle", "error"].includes(statusRef.current)) {
      return;
    }
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined" ||
      !MediaRecorder.isTypeSupported(MIME_TYPE)
    ) {
      setStatus("error");
      onErrorRef.current("Audioaufnahmen werden von diesem Browser nicht unterstützt.");
      return;
    }

    const sessionId = ++sessionIdRef.current;
    setStatus("requesting_permission");

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (error) {
      if (sessionId === sessionIdRef.current) {
        setStatus("error");
        onErrorRef.current(getUserMediaErrorMessage(error));
      }
      return;
    }

    if (sessionId !== sessionIdRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    let audioContext: AudioContext | null = null;
    try {
      audioContext = new AudioContext();
      if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => {});
      }
      if (sessionId !== sessionIdRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        await audioContext.close();
        return;
      }

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      const recorder = new MediaRecorder(stream, {
        mimeType: MIME_TYPE,
        audioBitsPerSecond: 64_000,
      });
      const session: VoiceSession = {
        id: sessionId,
        stream,
        recorder,
        audioContext,
        source,
        analyser,
        levelData: new Uint8Array(analyser.fftSize),
        chunks: [],
        startedAt: performance.now(),
        trackEndedHandler: () => {},
        maxDurationTimer: null,
        finishMode: null,
      };

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          session.chunks.push(event.data);
        }
        if (
          statusRef.current === "recording" &&
          performance.now() - session.startedAt >= maxDurationSeconds * 1000 - ENCODER_SAFETY_MARGIN_MS
        ) {
          stop("insert");
        }
      };
      recorder.onstop = () => {
        void finishRecording(session);
      };
      recorder.onerror = () => {
        if (session.id !== sessionIdRef.current) {
          return;
        }
        sessionIdRef.current++;
        sessionRef.current = null;
        releaseSessionResources(session);
        setStatus("error");
        onErrorRef.current("Die Aufnahme ist fehlgeschlagen.");
      };
      session.trackEndedHandler = () => {
        if (session.id !== sessionIdRef.current || statusRef.current !== "recording") {
          return;
        }
        cancel();
        setStatus("error");
        onErrorRef.current("Die Verbindung zum Mikrofon wurde unterbrochen.");
      };
      stream.getTracks().forEach((track) => {
        track.addEventListener("ended", session.trackEndedHandler, { once: true });
      });

      sessionRef.current = session;
      recorder.start(1000);
      session.maxDurationTimer = setTimeout(
        () => stop("insert"),
        Math.max(0, maxDurationSeconds * 1000 - ENCODER_SAFETY_MARGIN_MS),
      );
      setStatus("recording");
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      if (audioContext && audioContext.state !== "closed") {
        audioContext.close().catch(() => {});
      }
      if (sessionId === sessionIdRef.current) {
        sessionRef.current = null;
        setStatus("error");
        onErrorRef.current("Die Aufnahme konnte nicht gestartet werden.");
      }
    }
  }, [cancel, finishRecording, maxDurationSeconds, setStatus, stop]);

  const getAudioLevel = useCallback((): number => {
    const session = sessionRef.current;
    if (!session || statusRef.current !== "recording") {
      return 0;
    }

    session.analyser.getByteTimeDomainData(session.levelData);
    let sumOfSquares = 0;
    for (let index = 0; index < session.levelData.length; index++) {
      const normalized = (session.levelData[index] - 128) / 128;
      sumOfSquares += normalized * normalized;
    }

    return Math.sqrt(sumOfSquares / session.levelData.length);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const cancelOnPageExit = () => cancel();
    addEventListener("pagehide", cancelOnPageExit);
    addEventListener("beforeunload", cancelOnPageExit);

    return () => {
      mountedRef.current = false;
      removeEventListener("pagehide", cancelOnPageExit);
      removeEventListener("beforeunload", cancelOnPageExit);
      cancel();
    };
  }, [cancel]);

  return { status, start, stop, cancel, getAudioLevel };
};
